import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { styled } from '@mui/system';
import Button from '@mui/material/Button';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { SaveRounded } from '@mui/icons-material';
import SpeakerNotesOutlinedIcon from '@mui/icons-material/SpeakerNotesOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import { useParams, Link } from 'react-router-dom';
import { downloadFile } from '#action/download';
import QRCodeIcon from '#component/icon/qrcode-icon';
import ReportDialog from '#component/device/overview/report-dialog';
import { deviceTypesQR } from '#component/device/dashboard/helper';
import { getContrastColor } from '#component/theme';
import DeviceProjectsTreePopup from '#component/device/dashboard/projects-tree';
import propTypes from './prop-types';

const StyledMenuItem = styled(MenuItem)(() => ({
  minWidth: 160
}));

const StyledAccountTreeIcon = styled(AccountTreeOutlinedIcon)(() => ({
  color: getContrastColor(),
  marginRight: 5
}));

const StyledSpeakerNotesIcon = styled(SpeakerNotesOutlinedIcon)(() => ({
  color: getContrastColor(),
  marginRight: 5
}));

const StyledSaveIcon = styled(SaveRounded)(() => ({
  color: getContrastColor(),
  marginRight: 5
}));

const StyledQRCodeIcon = styled(QRCodeIcon)(() => ({
  color: getContrastColor(),
  marginRight: 5
}));

/**
 * Renders Mui IconButton with menu actions
 * to save report in PDF, download device label and redirect to settings page.
 * @param {*} props
 * @returns {React.FC}
 */

const Actions = props => {
  const { device, isAnyMetric, children, isInRange } = props;
  const [anchorEl, setAnchorEl] = useState(null);
  const [showReportDialog, setReportDialog] = useState(false);
  const [showDetailsDialog, setDetailsDialog] = useState(false);
  const { deviceId } = useParams();
  const dispatch = useDispatch();

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDownloadLabelClick = async () => {
    handleClose();
    const path = `report/device/${deviceId}/label`;
    const fileName = `Label ${device.title || device.uid}.pdf`;

    await dispatch(downloadFile(
      path,
      fileName
    ));
  };

  const menuItemsToRender = {
    export: isAnyMetric,
    label: deviceTypesQR.includes(device?.type)
  };

  if (!Object.values(menuItemsToRender).some(v => v)) {
    return children;
  }

  return (
    <>
      <Tooltip title="Device actions">
        <Button onClick={handleClick}>
          {children}
          <ArrowDropDownIcon />
        </Button>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <StyledMenuItem
          onClick={() => {
            setAnchorEl(null);
            setDetailsDialog(true);
          }}
        >
          <StyledAccountTreeIcon />
          Details
        </StyledMenuItem>
        <StyledMenuItem
          component={Link}
          to={`/device-log?device=${deviceId}`}
        >
          <StyledSpeakerNotesIcon />
          <span>Audit trail</span>
        </StyledMenuItem>
        {menuItemsToRender.export && isInRange &&
          <StyledMenuItem
            key="1"
            onClick={() => {
              setAnchorEl(null);
              setReportDialog(true);
            }}
          >
            <StyledSaveIcon />
            Report
          </StyledMenuItem>}
        {menuItemsToRender.label &&
        <StyledMenuItem onClick={handleDownloadLabelClick}>
          <StyledQRCodeIcon />
          Label
        </StyledMenuItem>}
      </Menu>
      <ReportDialog
        showDialog={showReportDialog}
        setReportDialog={setReportDialog}
        deviceId={deviceId}
        dispatch={dispatch}
        {...props}
      />
      {showDetailsDialog && <DeviceProjectsTreePopup
        open={showDetailsDialog}
        setOpen={setDetailsDialog}
        deviceUid={device.uid}
        deviceTitle={device.title}
      />}
    </>
  );
};

export default Actions;

Actions.propTypes = propTypes;
